import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * PageHeader — top-of-page title block. Backwards-compatible with the
 * old `Masthead` API so call sites keep working.
 */
export function Masthead({
  kicker,
  title,
  dateline,
  lede,
  actions,
  className,
}: {
  kicker?: string;
  title: ReactNode;
  dateline?: string;
  lede?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-col gap-4 pb-6', className)}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          {kicker && (
            <div className="flex items-center gap-2 animate-rise-fade" style={{ animationDelay: '0ms' }}>
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
              <span className="kicker text-brand-700">{kicker}</span>
            </div>
          )}
          <h1
            className="text-display-md font-bold tracking-head text-ink animate-rise-fade lg:text-[2.125rem]"
            style={{ animationDelay: '60ms' }}
          >
            {title}
          </h1>
          {lede && (
            <p
              className="max-w-2xl text-[0.9375rem] leading-relaxed text-ink-muted animate-rise-fade"
              style={{ animationDelay: '120ms' }}
            >
              {lede}
            </p>
          )}
          {dateline && (
            <p
              className="text-[0.8125rem] font-medium text-ink-dim animate-rise-fade"
              style={{ animationDelay: '160ms' }}
            >
              {dateline}
            </p>
          )}
        </div>
        {actions && (
          <div
            className="flex flex-wrap items-center gap-2 animate-rise-fade"
            style={{ animationDelay: '200ms' }}
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
